import React, { useCallback, useMemo } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import Button from '@mui/material/Button';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ConfirmModal from '@/common/containers/Modals/ConfirmModal';
import { useCurrentDecedentId } from '@/app/providers/DecedentProvider';
import { routes } from '@/app/routing';
import { Notification } from '@/common/utils';
import {
  useQuestionListQuery,
  useSubmitAnswersMutation,
} from '@/entities/question';
import {
  FINANCIAL_INSTITUTION_SLUG,
  findQuestionPathBySlug,
  getQuestionAnswerPayload,
  getQuestionsFromList,
} from '@/modules/financials/utils';

const FinancialInstitutionDeleteButton = ({
  institution,
  isLastInstitution,
  refreshTask,
  disabled,
  ...props
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const decedentId = useCurrentDecedentId();
  const { data: mainQuestionsData } = useQuestionListQuery('main', decedentId);
  const submitAnswersMutation = useSubmitAnswersMutation();

  const institutionQuestionPath = useMemo(() => {
    const mainQuestions = mainQuestionsData?.list
      ? getQuestionsFromList(mainQuestionsData.list)
      : mainQuestionsData?.questions || [];

    return findQuestionPathBySlug(mainQuestions, FINANCIAL_INSTITUTION_SLUG);
  }, [mainQuestionsData]);
  const institutionQuestion =
    institutionQuestionPath?.[institutionQuestionPath.length - 1];
  const parentQuestion =
    institutionQuestionPath?.[institutionQuestionPath.length - 2];
  const parentAnswer = useMemo(
    () =>
      (mainQuestionsData?.answers || []).find(
        (answer) =>
          answer.questionId === parentQuestion?.id && answer.status === 'active'
      ),
    [mainQuestionsData?.answers, parentQuestion?.id]
  );

  const deleteTitle = institution?.name
    ? t('dataEntry.archiveDeleteTitleWithValues', {
        item: t('Financial institution'),
        values: institution.name,
      })
    : t('dataEntry.archiveDeleteTitle', {
        item: t('Financial institution'),
      });
  const loading = submitAnswersMutation.isPending;
  const isDisabled = disabled || !institution?.id || !institutionQuestion?.id;

  const handleDelete = useCallback(async () => {
    if (!institution?.id || !institutionQuestion?.id) {
      return;
    }

    try {
      const answers = [
        {
          questionId: institutionQuestion.id,
          answerId: institution.id,
          value: '',
          action: 'archive',
        },
      ];

      if (isLastInstitution && parentQuestion?.id) {
        answers.push(
          getQuestionAnswerPayload({
            question: parentQuestion,
            answer: parentAnswer,
            value: 'No',
          })
        );
      }

      await submitAnswersMutation.mutateAsync({
        answers,
        decedentId,
        section: 'main',
        sectionType: 'Question',
      });

      Notification.success(t('Financial institution archived successfully'));
      history.push(routes.dashboard.finInstitutions);

      try {
        await refreshTask?.();
      } catch {
        // Deletion already succeeded; don't block navigation on a refresh error.
      }
    } catch {
      Notification.error(t('Failed to archive financial institution'));
    }
  }, [
    decedentId,
    history,
    institution,
    institutionQuestion,
    isLastInstitution,
    parentAnswer,
    parentQuestion,
    refreshTask,
    submitAnswersMutation,
    t,
  ]);

  return (
    <ConfirmModal
      type="delete"
      title={deleteTitle}
      description={t('financialInstitution.archiveDeleteDescription')}
      confirmText={t('delete')}
    >
      {(confirm) => (
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          disabled={isDisabled || loading}
          loading={loading}
          onClick={confirm(handleDelete)}
          sx={{ width: 'fit-content' }}
          {...props}
        >
          {t('Delete institution')}
        </Button>
      )}
    </ConfirmModal>
  );
};

export default FinancialInstitutionDeleteButton;
